<template>
    <b-modal
        :ref="modalRef"
        title-tag="h2"
        :title="`Edit Refund #${refund.index}`"
        lazy
        no-close-on-backdrop
        no-close-on-esc
    >
        <b-form-group label-for="date" label="Date:" label-cols="4">
            <div class="p-2" >
                {{ convertDate(refund.refund_date) }}
            </div>
        </b-form-group>
        <b-form-group label-for="client_id" label="Client:" label-cols="4">
            <div class="p-2" v-if="selectedClient">
                {{ selectedClient.label }}
            </div>
        </b-form-group>
        <b-form-group label-for="amount" label="Amount:" label-cols="4">
            <div class="p-2" >
                {{ _toCurrency(refund.amount) }}
            </div>
        </b-form-group>
        <b-form-group label-for="refund-type" label="Type:" label-cols="4">
            <div class="p-2" v-if="!notIntergratedPayment(refund.type)">
                {{ refundTypeName }}
            </div>
            <select
                v-else
                v-model="selectedType"
                v-validate="'required'"
                :class="{ 'border-danger': errors.has('refund-type') }"
                id="refund-type"
                name="refund-type"
                class="form-control"
                aria-invalid="false"
            >
                <option :value="null" disabled
                    >Please select a refund type (required)</option
                >
                <option
                    v-for="item in refundTypeOptions"
                    :value="item.id"
                    :key="item.id"
                >
                    {{ item.name }}
                </option>
            </select>
        </b-form-group>

        <b-form-group label-for="note" label="Notes:" label-cols="4">
            <b-form-textarea
                v-model="notes"
                placeholder="Notes (optional)"
                rows="3"
                name="note"
                :disabled="isCardConnectTerminal"
            ></b-form-textarea>
        </b-form-group>

        <template v-slot:modal-footer>
            <modal-footer
                v-if="!isCardConnectTerminal"
                :is-loading="isLoading"
                ok-title="Save"
                @ok-action="save"
                @cancel-action="close"
            />
            <div class="d-flex justify-content-between w-100" v-else>
                <!-- prop margin 2 to set the margin of loader m-2 -->
                <loader v-if="isLoading" :margin="2" />

                <b-button variant="secondary" @click="close" v-else>
                    Cancel
                </b-button>
            </div>
        </template>
    </b-modal>
</template>
<script type="text/javascript">
/**
 * @name RefundEdit
 * @description Edit refund
 * @see /apps/application/controllers/billing/refund.php for disable notes on cconnect terminal
 */

import { clone, findIndex } from "lodash";
import BillingRefund from "@/services/billing/billing-refund";
import Payment from "@/services/payment";

const NAME_CCONNECT_TERMINAL = "Credit Card On Terminal (CardConnect)";
/**
 * @method ReturnDeleteModal
 */
export default {
    components: {
        modalFooter: () => import("@/components/elements/modal-footer")
    },
    props: {
        modalRef: {
            type: String,
            required: true
        },
        refund: {
            type: Object,
            default() {
                return {};
            }
        },
        clientName: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            notes: null,
            amount: null,
            refundDate: null,
            selectedType: null,
            selectedClient: null,
            datePickerOptions: {
                useCurrent: true,
                format: "DD/MM/YYYY"
            },
            refundTypeOptions: [],
            ccPaymentTypes: [],
            isLoading: false
        };
    },
    computed: {
        hasReturnOrPayment() {
            return (
                this.refund.reeturn ||
                this.refund.isHasReeturn ||
                this.refund.payment_id
            );
        },
        refundTypeName() {
            return this.refund &&
                (this.refund.refundTypeDetail || this.refund.refund_type)
                ? (this.refund.refundTypeDetail &&
                      this.refund.refundTypeDetail.name) ||
                      (this.refund.refund_type && this.refund.refund_type.name)
                : null;
        },
        // based on /apps/application/controllers/billing/refund.php
        isCardConnectTerminal() {
            if (this.refundTypeName) {
                return (
                    this.refundTypeName.toLowerCase().replace(/\s+/g, "") ==
                    NAME_CCONNECT_TERMINAL.toLowerCase().replace(/\s+/g, "")
                );
            }
            return true;
        }
    },
    methods: {
        open() {
            this.$refs[this.modalRef].show();
            this.getRefundTypeOptions();
            this.getPaymentTypes();

            this.$nextTick(() => {
                this.setDate();
                this.setClient();
                this.amount = clone(this.refund.amount);
                this.notes = clone(this.refund.note);
                this.selectedType = clone(this.refund.type);
            });
        },
        setClient() {
            let label = this.clientName
                ? this.clientName
                : this.refund.client.display_name;
            this.selectedClient = { id: this.refund.client_id, label };
        },
        setDate() {
            this.datePickerOptions.format = this.getDateFormat();
            this.refundDate = this.convertDate(this.refund.refund_date);
        },
        convertDate(date) {
            let convertedDate = this._carbonDateDisplay(date);

            // FROM INVENTORY RETURN LIST, THE DATE IS IN STRING NOT CARBON
            if (convertedDate === "--") {
                convertedDate = this._setDateDisplay(date);
            }
            return convertedDate;
        },
        getRefundTypeOptions() {
            Payment.getPaymentType({ is_filter_funds: true }).then(response => {
                this.refundTypeOptions = response.resources.payment_types;
            });
        },
        getPaymentTypes() {
            Payment.getCreditCardPayments().then(response => {
                this.ccPaymentTypes = response.resources;
            });
        },
        notIntergratedPayment(refundTypeId) {
            let matchIndex = findIndex(this.ccPaymentTypes, function (element) {
                return element.id == refundTypeId;
            });

            return matchIndex == -1;
        },
        async save() {
            let valid = await this.$validator.validateAll();
            if (!valid) {
                return false;
            }

            this.isLoading = true;

            const params = {
                type: this.selectedType,
                note: this.notes
            };

            if (!this.hasReturnOrPayment) {
                (params.refund_date = this._fromDateStrToDateTime(
                    this.refundDate,
                    true
                )),
                    (params.client_id = this.selectedClient.id);
                params.amount = this.amount;
            }

            BillingRefund.saveRefund(this.refund.id, params)
                .then(response => {
                    if (response.code == 200) {
                        this._message(response.messages);
                        this.$emit("reload");
                    } else {
                        throw response.messages;
                    }

                    this.close();
                })
                .catch(error => {
                    this._message("Error : Unable to save refund", "danger");
                    this.close();
                    console.error(error);
                });
        },
        close() {
            this.isLoading = false;
            this.$refs[this.modalRef].hide();
        }
    }
};
</script>
